import { ParsedUrlQuery } from 'querystring';

import { useMemo } from 'react';

import { useSearchParams } from 'next/navigation';

import SearchBarSearchInput from '@components/ui/search/SearchBarInputNext/SearchBarSearchInput/SearchBarSearchInput';
import CustomIcon from '@components/utils/CustomIcon';
import { PlaneLanding, PlaneTakeOff, ChangePromotionIcon } from '@assets/icons';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import {
  SearchBarAirportSearchProps,
  SearchBarInputError,
} from '@components/views/flight/types';
import { useDebounceValue } from '@hooks/hooks';
import { useAirportSearchGet } from '@components/views/flight/hooks/flight.query';
import { AirportSearchViewModel } from '@api/index';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import palette from '@utils/theme/palette';
import { paramsToObject } from '@components/views/utils';

import { IconContainer, IconCircle } from '../FlightSearchStyle';
import {
  AirportsContainer,
  AirportWrapper,
  ArrivalAirportIconWrapper,
} from './SearchBarAirportSearchStyle';
import AirportSearchResultList from './AirportSearchResultList';

export default function AirportSearch({
  departureAirportError,
  arrivalAirportError,
  departureAirportSearchText,
  onDepartureAirportTextChange,
  onDepartureAirportSelect,
  arrivalAirportSearchText,
  onArrivalAirportTextChange,
  onArrivalAirportSelect,
  className,
  swapAirports,
  isBookerSearch,
  setButtonDisable,
  departureAirport,
  arrivalAirport,
  isDateError,
  borderRadius,
  isHomePage,
}: SearchBarAirportSearchProps & {
  departureAirport?: AirportSearchViewModel;
  arrivalAirport?: AirportSearchViewModel;
  departureAirportError: SearchBarInputError | boolean;
  arrivalAirportError: SearchBarInputError | boolean;
  className?: string;
  swapAirports: () => void;
  isBookerSearch?: boolean;
  setButtonDisable?: React.Dispatch<React.SetStateAction<boolean>>;
  isDateError: boolean;
  borderRadius?: string;
  isHomePage?: boolean;
}) {
  const debouncedDepartureSearchText = useDebounceValue(
    departureAirportSearchText
  );
  const debouncedArrivalSearchText = useDebounceValue(arrivalAirportSearchText);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const {
    data: departureAirportData,
    isInitialLoading: isDepartureAirportDataLoading,
    isError: isDepartureDataError,
  } = useAirportSearchGet({
    airportName: debouncedDepartureSearchText,
    select: (airports) => airports,
  });

  const {
    data: arrivalAirportData,
    isInitialLoading: isArrivalAirportDataLoading,
    isError: isArrivalDataError,
  } = useAirportSearchGet({
    airportName: debouncedArrivalSearchText,
    select: (airports) => airports,
  });
  useSkipEffectOnMount(() => {
    if (
      departureAirport?.airportCode ||
      query.dep ||
      arrivalAirport?.airportCode ||
      query.arr
    ) {
      if (!isDateError) {
        setButtonDisable?.(
          departureAirport?.airportCode != query.dep ||
            arrivalAirport?.airportCode != query.arr
        );
      }
    }
  }, [query, departureAirport, arrivalAirport]);
  return (
    <AirportsContainer isHomePage={isHomePage} className={className}>
      <LocationAnimationErrorBox
        borderRadius={borderRadius}
        hasError={!!departureAirportError}
      >
        <AirportWrapper>
          <SearchBarSearchInput
            isFlight={true}
            isBookerSearch={isBookerSearch}
            label='Nereden Gideceksiniz?'
            inputProps={{
              value: departureAirportSearchText,
              placeholder: 'Havalimanı veya Şehir',
              onChange: (e) =>
                onDepartureAirportTextChange(
                  (e.target as HTMLInputElement).value
                ),
            }}
            searchTextError={isDepartureDataError}
            leftIcon={() => (
              <CustomIcon
                fontSize={28}
                paletteColor='coral'
                icon={PlaneTakeOff}
              />
            )}
            rightIcon={() => null}
            SearchResultListComponent={({ onChange }) => (
              <AirportSearchResultList
                searchText={debouncedDepartureSearchText}
                onSelect={(item) => {
                  onChange(item?.airportName);
                  onDepartureAirportSelect(item);
                }}
                data={departureAirportData || []}
                isLoading={isDepartureAirportDataLoading}
              />
            )}
          />
        </AirportWrapper>
      </LocationAnimationErrorBox>
      <IconContainer>
        <IconCircle
          onClick={() => {
            swapAirports();
          }}
        >
          <CustomIcon
            fontSize={20}
            icon={ChangePromotionIcon}
            fill={palette.darkGray}
          />
        </IconCircle>
      </IconContainer>
      <LocationAnimationErrorBox
        borderRadius={borderRadius}
        hasError={!!arrivalAirportError}
      >
        <AirportWrapper>
          <SearchBarSearchInput
            label='Nereye Gideceksiniz?'
            isBookerSearch={isBookerSearch}
            inputProps={{
              value: arrivalAirportSearchText,
              placeholder: 'Havalimanı veya Şehir',
              onChange: (e) =>
                onArrivalAirportTextChange(
                  (e.target as HTMLInputElement).value
                ),
            }}
            searchTextError={isArrivalDataError}
            leftIcon={() => (
              <ArrivalAirportIconWrapper>
                <CustomIcon
                  fontSize={28}
                  paletteColor='coral'
                  icon={PlaneLanding}
                />
              </ArrivalAirportIconWrapper>
            )}
            popoverProps={{
              align: 'end',
            }}
            SearchResultListComponent={({ onChange }) => (
              <AirportSearchResultList
                searchText={debouncedArrivalSearchText}
                onSelect={(item) => {
                  onChange(item?.airportName);
                  onArrivalAirportSelect(item);
                }}
                data={arrivalAirportData || []}
                isLoading={isArrivalAirportDataLoading}
              />
            )}
          />
        </AirportWrapper>
      </LocationAnimationErrorBox>
    </AirportsContainer>
  );
}
